/**
 * Oct 20 (Issue 03A, FD-05) — "Customer Picked Up" from the FOH Workboard.
 *
 * The SAME authority and the SAME follow-ups as OptiBoard / Birdseye (OptiboardTicketRow.handleMarkPickedUp):
 * mark_completed_ticket_picked_up records the departure, releases the bay / spot, closes active custody and
 * writes the audit act in one transaction (20270207200300). The client then derives the timeline event (new
 * pickups only) and tells every mounted surface — here and on other devices — to re-read.
 *
 * Presentation-agnostic: the caller shows the returned words.
 */
import {
  markCompletedTicketPickedUp,
  isPickupSettled,
  didPickUpNow,
  needsPickupReload,
  describePickupRefusal,
} from '@/utils/completedPickupLifecycle';
import { logTicketEventToOcis } from '@/utils/ocisIngest';
import { notifyPickupChanged } from '@/utils/pickupConvergence';

export interface PickupActFeedback {
  /** The server settled the act (picked up now, or already picked up). */
  settled: boolean;
  /** The result is unknown: reload, never replay. */
  reload: boolean;
  tone: 'success' | 'info' | 'error';
  title: string;
  description?: string;
}

export async function recordCompletedPickup(ticketId: string, actorUserId: string | null): Promise<PickupActFeedback> {
  const pickup = await markCompletedTicketPickedUp(ticketId);

  // Unknown result: the handover may or may not have committed. Reload rather than replay.
  if (needsPickupReload(pickup.outcome)) {
    return { settled: false, reload: true, tone: 'error', title: 'Pickup result unconfirmed', description: describePickupRefusal(pickup) };
  }
  if (!isPickupSettled(pickup.outcome)) {
    return { settled: false, reload: false, tone: 'error', title: 'Cannot mark as picked up', description: describePickupRefusal(pickup) };
  }

  // The timeline event projects the singular human act: new pickups only.
  if (didPickUpNow(pickup.outcome) && pickup.shopId && actorUserId) {
    logTicketEventToOcis({
      ticketId,
      shopId: pickup.shopId,
      eventType: 'ticket_status_changed',
      content: 'Vehicle picked up by customer',
      authorUserId: actorUserId,
      payload: { reason: 'vehicle_picked_up', picked_up: true, picked_up_at: pickup.pickedUpAt },
    }).catch(err => console.error('[recordCompletedPickup] OCIS log error:', err));
  }

  notifyPickupChanged({
    isNewPickup: didPickUpNow(pickup.outcome), ticketId,
    shopId: pickup.shopId, pickedUpAt: pickup.pickedUpAt, act: 'completed',
  });

  return didPickUpNow(pickup.outcome)
    ? { settled: true, reload: false, tone: 'success', title: 'Marked as picked up', description: 'The vehicle’s space is free and the shop no longer holds it.' }
    : { settled: true, reload: false, tone: 'info', title: 'Already picked up', description: 'This vehicle had already been handed back. The board has been refreshed.' };
}
